import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ItemOrAbility } from "~/pages/CharacterSheet/types";
import ItemCard from "~/pages/CharacterSheet/widgets/ItemCard/ItemCard";
import styles from "~/pages/CharacterSheet/widgets/ItemsSection/ItemsSection.scss";

export interface ItemsSectionProps {
  items: ItemOrAbility[];
  onAddItem: (prefix: string, defaultContent: string) => void;
  onToggleEdit: (id: string) => void;
  onUpdate: (id: string, content: string) => void;
  onDelete: (id: string, skipConfirm?: boolean) => void;
  onMove: (id: string, direction: "up" | "down") => void;
  onReorder: (activeId: string, overId: string) => void;
}

const ItemsSection: React.FC<ItemsSectionProps> = ({
  items,
  onAddItem,
  onToggleEdit,
  onUpdate,
  onDelete,
  onMove,
  onReorder,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      onReorder(active.id.toString(), over.id.toString());
    }
  };

  return (
    <section className={styles.itemsSection}>
      <div className={styles.itemsHeader}>
        <h3 className={styles.sectionTitle}>Items & Abilities</h3>
        <div className={styles.itemsHelpText}>
          <strong>Enter</strong> to edit • <strong>Arrows</strong> to navigate • <strong>Alt + Arrows</strong> to move
        </div>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className={styles.itemsList}>
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {items.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                onToggleEdit={onToggleEdit}
                onUpdate={onUpdate}
                onDelete={onDelete}
                onMove={onMove}
              />
            ))}
          </SortableContext>
        </div>
      </DndContext>
      <div className={styles.itemActions} style={{ borderTop: "none", marginTop: "1rem" }}>
        <button
          type="button"
          onClick={() => onAddItem("[Item]", "New Item")}
          className={styles.addItemBtn}
          style={{ marginTop: 0 }}
        >
          + Item
        </button>
        <button
          type="button"
          onClick={() => onAddItem("[Ability]", "New Ability")}
          className={styles.addItemBtn}
          style={{ marginTop: 0 }}
        >
          + Ability
        </button>
      </div>
    </section>
  );
};

export default ItemsSection;
